import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import cl from 'classnames';
import { HEADSETS } from 'src/constants';
import { Battery4chart } from 'src/models';
import { formatShortDate } from 'src/helper';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import { useAppDispatch } from 'src/store';
import { fetchBatteryLogsAction, useBatteryState } from 'src/services/battery';
import { useHeadsetById } from 'src/services/headsets';
import {
  BatteryStatus,
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  Switcher,
  useStickyCols,
} from 'src/components';
import {
  CalendarDate,
  FilterModal,
  FilterModalButton,
  FilterModalIndicator,
  GroupFilter,
  SelectWithLabel,
  useDateRangePicker,
} from 'src/components/general';
import { BatteryLogsChart, ChartType } from './components/batteryLogChart';
import styles from './headsetBatteryLogsStyles.module.scss';

const batteryFilter = [
  'onlyOnCharge',
  'onlyFromBattery',
  'chargeLess30',
  'chargeMore90',
  'dischargeIsCalculated',
] as const;

type BatteryFilter = (typeof batteryFilter)[number];

const logsTypes = ['table', 'chart'] as const;
type LogsType = (typeof logsTypes)[number];

export const HeadsetBatteryLogs = () => {
  const { hotel: companyId, headset: headsetId } = useParams() as {
    hotel: string;
    headset: string;
  };
  const {
    access: { accessMap },
  } = usePage();
  const {
    header: { width: headerWidth },
  } = useContextData();
  const dispatch = useAppDispatch();
  const { t, currentLang } = useLanguage();
  const headset = useHeadsetById(headsetId);
  const { isPending, logs } = useBatteryState();
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [logsType, setLogsType] = useState<LogsType>('table');
  const onLogsTypeChange = useCallback((value: string) => {
    setLogsType(value as LogsType);
  }, []);

  const switcherList = useMemo(
    () =>
      logsTypes.map((type) => ({
        id: type,
        label: t(`page.headset.battery.tabs.${type}`),
      })),
    [t],
  );

  const filterList: { id: BatteryFilter; label: string }[] = useMemo(
    () =>
      batteryFilter.map((id) => ({
        id,
        label: t(`page.headset.battery.filters.${id}`),
      })),
    [t],
  );

  const [filterSet, setFilterSet] = useState<Set<BatteryFilter>>(
    new Set(['onlyFromBattery', 'dischargeIsCalculated']),
  );
  const [filterSetModal, setFilterSetModal] = useState<Set<BatteryFilter>>(
    new Set(filterSet),
  );

  const [chartType, setChartType] = useState<ChartType>(ChartType.lineWithArea);
  const [chartTypeModal, setChartTypeModal] = useState<ChartType>(chartType);
  const chartTypes = useMemo(
    () =>
      Object.values(ChartType).map((ctype) => ({
        value: ctype,
        label: t(`page.headset.battery.tabs.type.${ctype}`),
      })),
    [t],
  );

  const filteredLogs = useMemo(() => {
    if (!filterSet.size || !logs.length) {
      return logs;
    }

    const chargeRate =
      (filterSet.has('chargeLess30') ? 1 : 0) +
      (filterSet.has('chargeMore90') ? 2 : 0);

    return logs.filter((log) => {
      if (filterSet.has('onlyOnCharge') && !log.isCharging) {
        return false;
      }

      if (filterSet.has('onlyFromBattery') && log.isCharging) {
        return false;
      }

      if (filterSet.has('dischargeIsCalculated') && !log.dischargeSpeedMah) {
        return false;
      }

      switch (chargeRate) {
        case 1:
          return log.batteryChargeLevel < 30;
        case 2:
          return log.batteryChargeLevel > 90;
        case 3:
          return log.batteryChargeLevel < 30 || log.batteryChargeLevel > 90;
        default:
          return true;
      }
    });
  }, [logs, filterSet]);

  const initValue = useMemo(
    () => ({
      start: new CalendarDate(),
      end: new CalendarDate(),
    }),
    [],
  );

  const {
    range,
    initRange,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    resetDRPToInit,
  } = useDateRangePicker({
    initValue,
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    withoutClear: true,
    disabled: isPending,
  });

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  const physical_id = useMemo(
    () => headset?.phyDeviceId ?? null,
    [headset?.phyDeviceId],
  );
  const isRangeChanged = useMemo(
    () =>
      range.start?.time !== initRange.start?.time ||
      range.end?.time !== initRange.end?.time,
    [
      initRange.start?.time,
      initRange.end?.time,
      range.start?.time,
      range.end?.time,
    ],
  );

  useEffect(() => {
    if (!physical_id || !range.start || !range.end) {
      return;
    }

    dispatch(
      fetchBatteryLogsAction({
        companyId,
        params: {
          start: range.start.format,
          end: range.end.format,
          physical_id,
        },
      }),
    );
  }, [dispatch, physical_id, headsetId, companyId, range, isRangeChanged]);

  useEffect(() => {
    setIsFiltered(isRangeChanged || Boolean(filterSet.size));
  }, [isRangeChanged, filterSet.size]);

  const config: StickyTableConfig<Battery4chart>[] = useMemo(
    () => [
      {
        title: t('page.headset.battery.tabs.axis.timestamp'),
        key: 'timestamp',
        field: 'timestamp',
        render: (value) => formatShortDate(value),
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.timestamp).getTime();
          const dateB = new Date(b.timestamp).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
      },
      {
        title: t('page.headset.battery.tabs.axis.batteryChargeLevel'),
        key: 'batteryChargeLevel',
        field: 'batteryChargeLevel',
        sorter: (a, b, sortOrder) => {
          const left = a.batteryChargeLevel || -1;
          const right = b.batteryChargeLevel || -1;
          return (left - right) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value, entity) => (
          <BatteryStatus
            battery={value}
            charging={Boolean(entity.isCharging)}
            className={styles.tag120}
          />
        ),
      },
      {
        title: t('page.headset.battery.tabs.axis.batteryVoltage'),
        key: 'batteryVoltage',
        field: 'batteryVoltage',
      },
      {
        title: t('page.headset.battery.tabs.axis.chargeCounter'),
        key: 'chargeCounter',
        field: 'chargeCounter',
        render: (value) => value || '---',
      },
      {
        title: t('page.headset.battery.tabs.axis.dischargeSpeed'),
        key: 'dischargeSpeed',
        field: 'dischargeSpeed',
        render: (value) => value || '---',
      },
      {
        title: t('page.headset.battery.tabs.axis.dischargeSpeedMah'),
        key: 'dischargeSpeedMah',
        field: 'dischargeSpeedMah',
        render: (value) => value || '---',
      },
      {
        title: t('page.headset.battery.tabs.axis.batteryTemperature'),
        key: 'batteryTemperature',
        field: 'batteryTemperature',
      },
      {
        title: t('page.headset.battery.tabs.axis.batteryTemperatureMax'),
        key: 'batteryTemperatureMax',
        field: 'batteryTemperatureMax',
      },
      {
        title: t('page.headset.battery.tabs.axis.batteryHealth'),
        key: 'batteryHealth',
        field: 'batteryHealth',
      },
    ],
    [t],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  const resetFilter = useCallback(() => {
    resetDRPToInit();
    setFilterSet(new Set());
    setChartType(ChartType.bar);
  }, [resetDRPToInit]);

  return (
    <div className={styles.container}>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applySCChanges();
          applyDRPChanges();
          setFilterSet(new Set(filterSetModal));
          setChartType(chartTypeModal);
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          setFilterSetModal(new Set());
          setChartTypeModal(chartType);
          resetSCChanges();
          resetDRPChanges();
        }}
      >
        <GroupFilter
          label={t('page.headset.battery.filters')}
          selectedIDs={filterSetModal}
          items={filterList}
          selectItem={(id, value) => {
            setFilterSetModal((prevState) => {
              const set = new Set(prevState);
              if (value) {
                set.add(id as BatteryFilter);
                if (id === 'onlyOnCharge' && set.has('onlyFromBattery')) {
                  set.delete('onlyFromBattery');
                }
                if (id === 'onlyFromBattery' && set.has('onlyOnCharge')) {
                  set.delete('onlyOnCharge');
                }
              } else {
                set.delete(id as BatteryFilter);
              }
              return set;
            });
          }}
          className={styles.filterSelectModal}
          size="small"
        />
        {modalDateRangePicker}
        {modalStickyColsFilter}
        <SelectWithLabel
          label={t('page.headset.battery.tabs.type')}
          value={chartTypeModal}
          items={chartTypes}
          selectItem={(value) => {
            setChartTypeModal(value as ChartType);
          }}
          design="filterModal"
          size="small"
          className={styles.typeMobileFilter}
        />
      </FilterModal>
      <div
        className={styles.topSection}
        style={{ minWidth: headerWidth, maxWidth: headerWidth }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[HEADSETS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <Switcher
            currentId={logsType}
            list={switcherList}
            onChange={onLogsTypeChange}
            onlyMobile
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSwitcherSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowFiltersModal}
              disabled={isPending}
            />
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={Boolean(filterSet.size)}
              disabled={isPending}
            />{' '}
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              disabled={isPending}
            />
          </div>
        </div>
        <div className={styles.typeSwitcherContainer}>
          <Switcher
            currentId={logsType}
            list={switcherList}
            onChange={onLogsTypeChange}
            onlyDesktop
          />
        </div>
        <div className={styles.filters}>
          <GroupFilter
            label={t('page.headset.battery.filters')}
            selectedIDs={filterSet}
            items={filterList}
            selectItem={(id, value) => {
              setFilterSet((prevState) => {
                const set = new Set(prevState);
                if (value) {
                  set.add(id as BatteryFilter);
                  if (id === 'onlyOnCharge' && set.has('onlyFromBattery')) {
                    set.delete('onlyFromBattery');
                  }
                  if (id === 'onlyFromBattery' && set.has('onlyOnCharge')) {
                    set.delete('onlyOnCharge');
                  }
                } else {
                  set.delete(id as BatteryFilter);
                }
                setFilterSetModal(new Set(set));
                return set;
              });
            }}
            className={styles.filterSelect}
          />
          {dateRangePicker}
          {stickyColsFilter}
          <SelectWithLabel
            label={t('page.headset.battery.tabs.type')}
            value={chartType}
            items={chartTypes}
            selectItem={(value) => {
              setChartType(value as ChartType);
              setChartTypeModal(value as ChartType);
            }}
            design="filter"
            className={styles.typeFilter}
          />
        </div>
      </div>
      <StickyTable
        config={config}
        data={filteredLogs}
        withPaginator
        stickyCols={stickyCols}
        className={cl(
          styles.containerContent,
          logsType === 'chart' && styles.hide,
        )}
        isLoading={isPending}
        isFiltered={isFiltered}
        resetFilter={resetFilter}
        notFoundText={t('general.resetFilters')}
      />
      <div
        className={cl(
          styles.containerContent,
          logsType === 'table' && styles.hide,
        )}
      >
        <div className={styles.charts}>
          <BatteryLogsChart filteredLogs={filteredLogs} chartType={chartType} />
        </div>
      </div>
    </div>
  );
};

export default HeadsetBatteryLogs;
